<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useRoute, useRouter } from 'vue-router';
import GuidedTour from 'dashboard/components-next/GuidedTour.vue';
import { useMeMostra } from 'dashboard/components-next/copilot/useMeMostra';
import { telaAberta } from 'dashboard/components-next/copilot/telaAberta';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import Copilot from 'dashboard/components-next/copilot/Copilot.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useNomeDoAgente } from 'dashboard/composables/useNomeDoAgente';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';

defineProps({
  conversationInboxType: {
    type: String,
    default: '',
  },
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

const currentUser = useMapGetter('getCurrentUser');
const allAssistants = useMapGetter('captainAssistants/getRecords');
// Pedido do Victor (2026-07-23, reforçado 2026-07-28): a bolha do Copilot
// (uso INTERNO do advogado, dentro do painel) não deve listar os
// assistentes que conversam com o cliente no WhatsApp (Andie e variantes
// por área) — só o assistente interno genérico (ex: "Lumos AI").
//
// Achado real 2026-07-28 (Victor testando ao vivo): a versão anterior
// deste filtro excluía só por `lumos_area_slug` (especialista por área) e
// `lumos_role: follow_up_persona` — mas o ROTEADOR (Andie, "Agente de
// Triagem") não tem nenhuma dessas 2 flags, então passava direto e
// aparecia no seletor da bolha junto com o Lumos AI. Fix: usa a
// convenção real do projeto (ver .claude/rules/ai-agents-lumos.md) —
// TUDO client-facing se chama "Andie", sem exceção, nunca "Lumos" na
// frente do cliente. Checar pelo nome é mais robusto que empilhar flag
// nova a cada padrão client-facing novo que aparecer.
//
// 08/10/2026 (Fase 0 do Lumos): o filtro passou a ser o PAPEL do assistente,
// o mesmo que o servidor confere (`Captain::Assistant#serve_de_copiloto?`).
// O nome do agente do escritório fica como segunda trava (10/10/2026: cada
// escritório escolhe o nome — "Andie" é o da A&T).
const PAPEIS_FORA_DA_BOLHA = ['roteador', 'especialista', 'redator'];
const { nomeDoAgente } = useNomeDoAgente();
const assistants = computed(() =>
  allAssistants.value.filter(
    a =>
      a.config?.enabled !== false &&
      !PAPEIS_FORA_DA_BOLHA.includes(a.papel) &&
      a.name !== nomeDoAgente.value
  )
);
const uiFlags = useMapGetter('captainAssistants/getUIFlags');
const inboxAssistant = useMapGetter('getCopilotAssistant');
const currentChat = useMapGetter('getSelectedChat');

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const selectedCopilotThreadId = ref(null);
const messages = computed(() =>
  store.getters['copilotMessages/getMessagesByThreadId'](
    selectedCopilotThreadId.value
  )
);

const selectedAssistantId = ref(null);

const activeAssistant = computed(() => {
  const preferredId = uiSettings.value.preferred_captain_assistant_id;

  // If the user has selected a specific assistant, it takes first preference for Copilot.
  if (preferredId) {
    const preferredAssistant = assistants.value.find(a => a.id === preferredId);
    // Return the preferred assistant if found, otherwise continue to next cases
    if (preferredAssistant) return preferredAssistant;
  }

  // If the above is not available, the assistant connected to the inbox takes preference.
  if (inboxAssistant.value) {
    const inboxMatchedAssistant = assistants.value.find(
      a => a.id === inboxAssistant.value.id
    );
    if (inboxMatchedAssistant) return inboxMatchedAssistant;
  }
  // If neither of the above is available, the first assistant in the account takes preference.
  return assistants.value[0];
});

const closeCopilotPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_copilot_panel_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};

const setAssistant = async assistant => {
  selectedAssistantId.value = assistant.id;
  await updateUISettings({
    preferred_captain_assistant_id: assistant.id,
  });
};

const shouldShowCopilotPanel = computed(() => {
  const { is_copilot_panel_open: isCopilotPanelOpen } = uiSettings.value;
  return isCopilotPanelOpen && !uiFlags.value.fetchingList;
});

// "Me mostra": o painel sai da frente durante o tour e volta no fim.
const { tour, mostrar } = useMeMostra({
  router,
  aoComecar: () => updateUISettings({ is_copilot_panel_open: false }),
  aoTerminar: () => updateUISettings({ is_copilot_panel_open: true }),
});

const handleReset = () => {
  selectedCopilotThreadId.value = null;
};

const sendMessage = async message => {
  try {
    if (selectedCopilotThreadId.value) {
      await store.dispatch('copilotMessages/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat.value?.id,
        threadId: selectedCopilotThreadId.value,
        message,
        tela: telaAberta(route),
      });
    } else {
      const response = await store.dispatch('copilotThreads/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat.value?.id,
        message,
        tela: telaAberta(route),
      });
      selectedCopilotThreadId.value = response.id;
    }
  } catch (error) {
    useAlert(error.message);
  }
};

// "Perguntar ao Lumos" de outra tela (o Briefing do Dia): abre o painel numa
// conversa nova e já pergunta. Sem assistente carregado, só abre o painel.
const perguntarDeFora = async pergunta => {
  await updateUISettings({
    is_copilot_panel_open: true,
    is_contact_sidebar_open: false,
  });
  if (!activeAssistant.value || !pergunta) return;
  handleReset();
  sendMessage(pergunta);
};

onMounted(() => {
  store.dispatch('captainAssistants/get');
  emitter.on(BUS_EVENTS.LUMOS_PERGUNTAR, perguntarDeFora);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.LUMOS_PERGUNTAR, perguntarDeFora);
});
</script>

<template>
  <div
    v-if="shouldShowCopilotPanel"
    v-on-click-outside="() => closeCopilotPanel()"
    class="copilot-floating bg-n-surface-2 overflow-hidden flex flex-col fixed z-40 ltr:right-4 rtl:left-4 bottom-4 w-[380px] max-w-[calc(100vw-2rem)] h-[620px] max-h-[calc(100vh-6rem)] rounded-2xl border"
  >
    <Copilot
      :messages="messages"
      :support-agent="currentUser"
      :conversation-inbox-type="conversationInboxType"
      :assistants="assistants"
      :active-assistant="activeAssistant"
      @set-assistant="setAssistant"
      @send-message="sendMessage"
      @reset="handleReset"
      @mostrar="mostrar"
    />
  </div>
  <GuidedTour
    :is-active="tour.isActive.value"
    :current-step="tour.currentStep.value"
    :current-step-index="tour.currentStepIndex.value"
    :total-steps="tour.totalSteps.value"
    :progress="tour.progress.value"
    :is-first-step="tour.isFirstStep.value"
    :is-last-step="tour.isLastStep.value"
    :target-rect="tour.targetRect.value"
    @next="tour.nextStep"
    @prev="tour.prevStep"
    @skip="tour.skipTour"
    @complete="tour.completeTour"
  />
</template>

<style scoped>
/* O painel nasce DA BOLHA: `transform-origin` no canto de baixo à direita,
   que é onde ela fica. Com a mola do produto (o mesmo `cubic-bezier` da pílula
   das abas e dos modais) ele passa um triz do alvo e assenta — a diferença
   entre "apareceu" e "veio de algum lugar".
   A borda ganha um fio iris: o painel é do Lumos, e a cor diz isso antes de
   qualquer texto. */
.copilot-floating {
  border-color: rgb(var(--iris-6));
  box-shadow:
    0 10px 40px -8px rgb(var(--iris-9) / 0.25),
    0 4px 12px rgb(15 23 42 / 0.12);
  animation: copilot-pop-in 0.32s cubic-bezier(0.34, 1.12, 0.44, 1);
  transform-origin: bottom right;
}

.dark .copilot-floating {
  border-color: rgb(var(--iris-6));
  box-shadow:
    0 10px 40px -8px rgb(var(--iris-9) / 0.3),
    0 4px 12px rgb(0 0 0 / 0.5);
}

@keyframes copilot-pop-in {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.94);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* No PWA a barra inferior (Dashboard/Chat/Interno/Mais) come os 64px de baixo
 * da tela. Com o `bottom-4` das utilitárias, o campo de escrever do Lumos
 * nascia atrás dela — o painel abria e não dava para digitar nada.
 * Sobe o painel acima da barra e encurta a altura para o topo não entrar
 * embaixo do cabeçalho. Só no celular; o desktop não tem essa barra. */
@media (max-width: 639px) {
  .copilot-floating {
    bottom: calc(5rem + env(safe-area-inset-bottom));
    max-height: calc(100vh - 11rem - env(safe-area-inset-bottom));
  }
}

@media (prefers-reduced-motion: reduce) {
  .copilot-floating {
    animation: none;
  }
}
</style>
